// src/components/Dealer/DealerStatusPanel.tsx

import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useState } from 'react';
import { dealerApi } from '../../services/dealerApi';
import type { Dealer, DealerStatusHistoryEntry } from '../../types/dealer';
import { Modal } from '../ui/modal';
import DealerStatusChangeForm from './DealerStatusChangeForm';
import { dealerStatusOf } from './dealerStatus';

interface DealerStatusPanelProps {
  dealer: Dealer;
  /** 종사원증 확인 대기가 남아 있으면 이용정지 해제 때 '확인 중'으로 돌아간다 */
  hasPendingLicense: boolean;
}

/** 기록 한 줄의 내용·메모 */
const describe = (row: DealerStatusHistoryEntry): { text: string; memo: string } => {
  if (row.event === 'license_approved') {
    const source = row.source === 'replace' ? '교체' : '가입';
    return { text: '종사원증 확인됨', memo: [source, row.number].filter(Boolean).join(' · ') };
  }

  return {
    text: `${dealerStatusOf(row.from ?? '').label} → ${dealerStatusOf(row.to ?? '').label}`,
    memo: row.memo ?? '',
  };
};

/**
 * 딜러 상세 — 딜러 상태와 이용정지·해제 버튼, 변경 기록.
 * 확인 중 → 활성은 여기서 하지 않고 종사원증 '확인됨으로 변경'으로만 (서버 규칙과 같음).
 */
export default function DealerStatusPanel({ dealer, hasPendingLicense }: DealerStatusPanelProps) {
  const [modal, setModal] = useState<'suspend' | 'unsuspend' | null>(null);
  const status = dealerStatusOf(dealer.status);

  const { data: historyData } = useQuery({
    queryKey: ['dealer-status-history', dealer.id],
    queryFn: () => dealerApi.getStatusHistory(dealer.id),
  });
  const history = historyData?.data ?? [];

  const openModal = (type: 'suspend' | 'unsuspend') => setModal(type);
  const closeModal = () => setModal(null);

  const canSuspend = dealer.status === 'active' || dealer.status === 'pending';
  const canUnsuspend = dealer.status === 'suspended';

  return (
    <div className='mt-6 border-t border-gray-200 pt-4 dark:border-gray-700'>
      <div className='mb-2 flex flex-wrap items-center justify-between gap-3'>
        <div className='flex items-center gap-2'>
          <p className='text-xs text-gray-500 dark:text-gray-400'>딜러 상태</p>
          <span className={`rounded px-1.5 py-0.5 text-xs font-medium ${status.className}`}>{status.label}</span>
        </div>
        <div className='flex gap-2'>
          {canSuspend && (
            <button
              type='button'
              onClick={() => openModal('suspend')}
              className='rounded-lg border border-red-300 bg-white px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-50 dark:border-red-800 dark:bg-gray-800 dark:text-red-400 dark:hover:bg-red-900/20'>
              이용정지
            </button>
          )}
          {canUnsuspend && (
            <button
              type='button'
              onClick={() => openModal('unsuspend')}
              className='rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'>
              이용정지 해제
            </button>
          )}
        </div>
      </div>
      {status.description && <p className='mb-3 text-sm text-gray-600 dark:text-gray-400'>{status.description}</p>}

      <div className='overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700'>
        <p className='border-b border-gray-200 bg-gray-50 px-4 py-2 text-xs font-medium text-gray-600 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400'>
          변경 기록
        </p>
        {history.length === 0 ? (
          <p className='px-4 py-3 text-sm text-gray-500 dark:text-gray-400'>변경 기록이 없습니다.</p>
        ) : (
          <div className='overflow-x-auto'>
            <table className='w-full min-w-[640px] text-sm'>
              <tbody>
                {history.map((row) => {
                  const { text, memo: rowMemo } = describe(row);
                  return (
                    <tr key={row.id} className='border-b border-gray-100 last:border-0 dark:border-gray-800'>
                      <td className='w-36 px-4 py-2.5 align-top text-gray-500 dark:text-gray-400'>
                        {row.at ? dayjs(row.at).format('YYYY.MM.DD HH:mm') : '-'}
                      </td>
                      <td
                        className={`w-32 px-4 py-2.5 align-top ${
                          row.admin ? 'text-gray-700 dark:text-gray-300' : 'text-gray-500 dark:text-gray-400'
                        }`}>
                        {row.admin ? `관리자 ${row.admin.name}` : '시스템'}
                      </td>
                      <td className='w-48 px-4 py-2.5 align-top font-medium text-gray-900 dark:text-white'>{text}</td>
                      <td className='px-4 py-2.5 align-top text-gray-600 dark:text-gray-400'>{rowMemo}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* 이용정지·해제 (신고 관리 딜러 요약 팝업과 같은 입력) */}
      <Modal isOpen={modal !== null} onClose={closeModal} className='mx-4 max-w-[480px]'>
        <div className='p-6'>
          {modal && (
            <DealerStatusChangeForm
              dealerId={dealer.id}
              dealerName={dealer.name}
              mode={modal}
              hasPendingLicense={hasPendingLicense}
              onDone={closeModal}
              onCancel={closeModal}
            />
          )}
        </div>
      </Modal>
    </div>
  );
}
